import { OdinIcon, OdinIconSize } from '@myosh/odin-components';
import clsx from 'clsx';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { JobStatus } from '../../redux/slices/background-job-slice';

export interface BaseJobPanelItemProps {
  title: string;
  subtitle?: string;
  status?: JobStatus | string; //TODO: Check if we can remove string
  onRemove: () => void;
  onClick?: () => void;
  icon?: ReactNode | React.JSX.Element;
  isDeleting?: boolean;
}

const BaseJobPanelItem = ({ title, subtitle, status, onRemove, onClick, icon, isDeleting }: BaseJobPanelItemProps) => {
  const { t } = useTranslation();
  const isClickable = (status === JobStatus.Success || status === 'FINISHED') && !!onClick;

  const containerClasses = clsx('flex items-center p-3 border-b border-solid border-gray-4', {
    '[&:has(div:hover)]:bg-primary-5': isClickable,
  });

  const contentClasses = clsx('flex gap-2 items-center min-w-0 w-full', {
    'hover:cursor-pointer': isClickable,
  });

  const removeIconClasses = clsx('w-4 h-4 rounded-full ml-2 hover:bg-primary-5 hover:cursor-pointer', {
    'pointer-events-none': status === JobStatus.InProgress || status === 'RUNNING' || isDeleting,
  });

  return (
    <div className={containerClasses}>
      <div className={contentClasses} onClick={isClickable ? onClick : undefined}>
        {icon && <span>{icon}</span>}
        <div className="flex-1 truncate">
          <span className="block text-base" title={title}>
            {title}
          </span>
          {subtitle && (
            <span className="block truncate text-xs font-light" title={subtitle}>
              {subtitle}
            </span>
          )}
        </div>
        <span>{getStatusIcon(status)}</span>
      </div>
      <span className={removeIconClasses} onClick={onRemove} title={t('remove')}>
        {isDeleting ? (
          <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />
        ) : (
          status !== JobStatus.InProgress &&
          status !== 'RUNNING' && <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />
        )}
      </span>
    </div>
  );
};

export default React.memo(BaseJobPanelItem);

// util
export const getStatusIcon = (status?: JobStatus | string) => {
  if (status) {
    if (status === JobStatus.InProgress || status === 'RUNNING') {
      return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />;
    } else if (status === JobStatus.Success || status === 'FINISHED') {
      return <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className="text-success" />;
    } else if (status === JobStatus.Failed || status === 'FAILED') {
      return <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className="text-error" />;
    }
  }
};
